import { NRC_REGEX, NRC_TYPES } from "@/constants";
import { useNRC } from "@/hooks";
import { cn } from "@/libs/cn";
import { convertEnglishToBurmeseNumerals, getNRCTownshipCode } from "@/libs/utils";
import { Form, FormInstance, Input, Select } from "antd";
import { useEffect, useState } from "react";

interface NRCInputProps {
    form: FormInstance,
    disabled?: boolean,
    rootWrapperClassName?: string,
    selectorsWrapperClassName?: string,
    inputWrapperClassName?: string,
}

export function NRCInput({
    form,
    disabled = false,
    rootWrapperClassName,
    selectorsWrapperClassName,
    inputWrapperClassName
}: NRCInputProps) {
    const [validationError, setValidationError] = useState<string | null>(null);

    const {
        nrcParts,
        townships,
        fullNRCString,
        onNRCNumberChange,
        onSelectChange
    } = useNRC(form.getFieldValue("nrc") ?? "");

    useEffect(() => {
        form.setFieldValue("nrc", fullNRCString);
        form.setFieldValue("nrc_number", nrcParts.nrc_number);
        const hasAllParts = Object.values(nrcParts).every(item => item !== "");

        if (hasAllParts &&
            fullNRCString &&
            !NRC_REGEX.test(fullNRCString)
        ) {
            setValidationError('Invalid NRC (e.g., ၆/ဘပန(နိုင်)၀၆၆၂၂၈)!');
            form.validateFields(['nrc'])
        } else {
            setValidationError(null);
        }

    }, [nrcParts, form, fullNRCString]);

    // Reset NRC parts when the component mounts
    useEffect(() => {
        setValidationError(null);
        if (form.getFieldValue('nrc') === "" || !form.getFieldValue('nrc')) {
            form.resetFields(['nrc_number'])
        }
    }, [form]);

    return (
        <div>
            <div
                className={cn("items-center gap-2 grid grid-cols-3", rootWrapperClassName)}
            >
                <Form.Item
                    name={"nrc"}
                    hidden
                    className="hidden"
                >
                    <Input
                        disabled={disabled}
                    />
                </Form.Item>

                <div
                    className={cn("grid grid-cols-14 col-span-2 items-center", selectorsWrapperClassName)}
                >
                    <Form.Item
                        label="NRC"
                        required={false}
                        className="col-span-4"
                    >
                        <Select
                            options={
                                Array(14).fill(0).map((_, index) => ({
                                    value: (index + 1).toString(),
                                    label: convertEnglishToBurmeseNumerals((index + 1).toString())
                                }))
                            }
                            placeholder="NRC Code"
                            value={nrcParts.nrc_code}
                            onChange={(value) => onSelectChange(value, 'nrc_code')}
                            size="large"
                            className=" [&>.ant-select-selector]:!rounded-r-none [&>.ant-select-selector]:!border-r-0"
                            disabled={disabled}
                        />
                    </Form.Item>
                    <Form.Item
                        label=" "
                        required={false}
                        className="col-span-6"
                    >
                        <Select
                            options={townships.map((ts) => ({
                                value: getNRCTownshipCode(ts),
                                label: getNRCTownshipCode(ts),
                            }))}
                            key={"township_code"}
                            placeholder="Township"
                            value={nrcParts.township_code}
                            onChange={(value) => onSelectChange(value, 'township_code')}
                            size="large"
                            className=" [&>.ant-select-selector]:!rounded-none"
                            disabled={disabled}
                            filterOption={(input, option) => !!option && option.label.toLowerCase().includes(input.toLowerCase())}
                            showSearch
                        />
                    </Form.Item>
                    <Form.Item
                        label=" "
                        required={false}
                        rules={[{ required: true, message: "Required!" }]}
                        initialValue={NRC_TYPES[0]}
                        className="col-span-4"
                    >
                        <Select
                            options={NRC_TYPES.map((type) => ({
                                value: type,
                                label: type
                            }))}
                            placeholder="NRC Type"
                            value={nrcParts.nrc_type}
                            onChange={(value) => onSelectChange(value, 'nrc_type')}
                            size="large"
                            className=" [&>.ant-select-selector]:!rounded-l-none [&>.ant-select-selector]:!border-l-0  "
                            disabled={disabled}
                        />
                    </Form.Item>
                </div>
                <Form.Item
                    label=" "
                    required={false}
                    className={cn("col-span-1", inputWrapperClassName)}
                    name={"nrc_number"}
                // rules={[
                //     ({ getFieldValue }) => ({
                //         validator(_, value) {
                //             if (!value) {
                //                 setValidationError('All NRC parts are required!');
                //                 return Promise.reject(new Error(""));
                //             }

                //             return Promise.resolve();
                //         }
                //     })
                // ]}
                >
                    <Input
                        // placeholder="၁၂၃၄၅၆"
                        pattern="[၀-၉]{5,6}|[0-9]{5,6}"
                        maxLength={6}
                        minLength={5}
                        onChange={onNRCNumberChange}
                        size="large"
                        disabled={disabled}
                    />
                </Form.Item>
            </div >
            {
                validationError && (
                    <p className="-mt-6" >
                        <span className="text-danger animate-fade-in ">
                            {validationError}
                        </span>
                    </p>
                )
            }
        </div>
    )
}
